import { Module } from 'vuex';

import { RootState } from 'frontend/types';
import { Lesson } from 'frontend/types/lessons/lesson';
import {
  LessonContextInputs,
  LessonEndstateContextResult,
  ErrorsDict,
  LessonUserReportInputs,
  LessonUserReportResult,
} from 'frontend/types/lessons/requests';
import { apiResolve } from 'frontend/router';
import { apiRequest } from 'frontend/store/modules/lessons/apiHelpers';
import { commatize } from 'frontend/utils/formatting';
import { deepCopyObj } from 'frontend/utils/copy';

interface LessonState {
  lesson: Lesson;
  requestErrors: string[];
  showingReportProblem: boolean;
}

const lessonModule: Module<LessonState, RootState> = {
  namespaced: true,
  state: {
    lesson: {} as Lesson,
    requestErrors: [],
    showingReportProblem: false,
  },

  mutations: {
    setValue(state: LessonState, value: Lesson) {
      state.lesson = value;
    },
    setErrors(state: LessonState, value: ErrorsDict) {
      state.requestErrors = value.requestErrors;
    },
    setReadOnly(state: LessonState) {
      const readonlyLesson = deepCopyObj(state.lesson);
      readonlyLesson.is_editable = false;
      state.lesson = readonlyLesson;
    },
    setShowingReportProblem(state: LessonState, value: boolean) {
      state.showingReportProblem = value;
    },
  },

  actions: {
    hideReportProblem({ commit }) {
      commit('setShowingReportProblem', false);
    },
    showReportProblem({ commit }) {
      commit('setShowingReportProblem', true);
    },
    async fetchEndstateContext(
      { commit },
      { params, query }: LessonContextInputs,
    ): Promise<LessonEndstateContextResult> {
      const route = apiResolve({ params, query, name: 'lesson_endstate_context' });
      const [success, result] = await apiRequest(commit, route.href, {});

      if (result && result.endstate_context) {
        // These manual commits are necessary because we aren't using the Vue router
        // to call into the endstate-context endpoint, so the leagues Vuex store doesn't
        // have its results (via self.add_results()) handled automatically.

        if (result.leagues) {
          commit(
            'leagueLessonXP/setValue',
            {
              xp_details: result.leagues.xp_details,
              earned_xp: result.leagues.earned_xp,
              zero_xp_message: result.leagues.zero_xp_message,
            },
            { root: true },
          );
          commit('leagueLeaderboard/setValue', result.leagues.user_league, { root: true });
          if (result.leagues.leagues_opt_out_or_locked_details) {
            commit(
              'leagueLeaderboard/setOptOutOrLockedDetails',
              result.leagues.leagues_opt_out_or_locked_details,
              { root: true },
            );
          }
        }

        return {
          success,
          data: {
            endstate_context: result.endstate_context,
            thumbs_feedback: result.thumbs_feedback || null,
          },
        };
      }
      return { success, data: undefined };
    },

    async userReport(
      { commit },
      { params, data }: LessonUserReportInputs,
    ): Promise<LessonUserReportResult> {
      const route = apiResolve({ params, name: 'lesson_report_problem' });
      const [success, result] = await apiRequest(commit, route.href, data);
      if (result) {
        return {
          success,
          data: {},
        };
      }
      return { success, data: undefined };
    },
  },

  getters: {
    lesson(state: LessonState): Lesson {
      return state.lesson;
    },
    lessonIsEditable(state: LessonState): boolean {
      return state.lesson.is_editable === true;
    },
    formattedActiveUsers(state: LessonState): string {
      return commatize(state.lesson.active_users);
    },
  },
};

export default lessonModule;
